/* Every job the signed-in person can see, a page at a time.
 *
 * The board answers "what is happening now"; this answers "where did J-1039 get
 * to" and "what else is Vivid White". Search covers the job code, the customer,
 * the title, their reference, the colour code, name and brand, and the stage —
 * because people search with whatever they happen to remember.
 *
 * Unlike the board, this list is **paged and sorted on the server**. The board
 * is one screen of the shop's working set and can be held whole; this is every
 * job the shop has ever done, and the archive only grows. So the sort, the
 * filter, the search and the page are all part of one request, and the store
 * holds them — changing any of them is a new question, not a re-render.
 *
 * Which is also why the sortable headings do not sort what is on screen. They
 * ask again. Sorting a page of 25 by promised date would order those 25 and
 * quietly leave the other 300 out of it, which looks right and is wrong. */
import { useEffect } from 'react';
import type { CSSProperties } from 'react';
import { areaFor } from '@/domain/items';
import { atRisk, isOverdue } from '@/domain/schedule';
import { formatDay } from '@/domain/time';
import { stageName, toneOf, useApp } from '@/store/useApp';
import { NO_AUTOFILL } from '@/ui/autofill';
import { PageHead, Pager, Seg, SortHeader } from '@/ui/controls';
import { Swatch } from '@/ui/Swatch';
import type { JobSort } from '@/types';
import { usePageGutter, withGutter } from '@/ui/viewport';

const FILTERS = [
  { label: 'All', key: 'all' },
  { label: 'In the shop', key: 'live' },
  { label: 'In progress', key: 'progress' },
  { label: 'Late', key: 'late' },
  { label: 'Delivered', key: 'done' },
] as const;

/** The three columns that can be ordered, and how each one reads once it is.
 *  Said in words rather than left to the arrow alone — the arrow is the second
 *  half of it, and a caption nobody has to decode is the first. */
const SORTABLE: Record<
  Exclude<JobSort, 'board'>,
  { heading: string; asc: string; desc: string }
> = {
  code: { heading: 'Job', asc: 'oldest job first', desc: 'newest job first' },
  promised: { heading: 'Promised', asc: 'earliest promise first', desc: 'newest promise first' },
  deadline: { heading: 'Deadline', asc: 'earliest deadline first', desc: 'latest deadline first' },
};

const cell: CSSProperties = { fontFamily: 'var(--font-data)', fontSize: 12.5 };

/** A sortable heading, wired to the store: which column the list is in order
 *  of, and what a click on this one would do. The heading itself, the caret and
 *  the `aria-sort` live in `ui/controls.tsx` — the customers list has the same
 *  headings, and two copies is how one of them stops matching the other. */
function JobSortHeader({ column }: { column: Exclude<JobSort, 'board'> }) {
  const sort = useApp((s) => s.jobSort);
  const dir = useApp((s) => s.jobDir);
  const sortJobsBy = useApp((s) => s.sortJobsBy);
  const on = sort === column;
  const { heading, asc, desc } = SORTABLE[column];

  return (
    <SortHeader
      heading={heading}
      on={on}
      dir={dir}
      onSort={() => sortJobsBy(column)}
      title={on ? `Sort ${dir === 'asc' ? desc : asc}` : `Sort ${column === 'code' ? asc : desc}`}
    />
  );
}

/** The foot of the list. */
function ListFoot() {
  const page = useApp((s) => s.listPage);
  const pages = useApp((s) => s.listPages);
  const total = useApp((s) => s.listTotal);
  const size = useApp((s) => s.listSize);
  const shown = useApp((s) => s.list.length);
  const status = useApp((s) => s.listStatus);
  const goToJobPage = useApp((s) => s.goToJobPage);
  const setJobPageSize = useApp((s) => s.setJobPageSize);

  return (
    <Pager
      page={page}
      pages={pages}
      total={total}
      shown={shown}
      size={size}
      busy={status === 'loading'}
      onPage={goToJobPage}
      onSize={setJobPageSize}
      empty="No jobs on this page"
    />
  );
}

export function JobsScreen() {
  const gutter = usePageGutter();
  const st = useApp();
  const { list: rows, listStatus: status, loadJobList } = st;

  // The page is a request, so opening the screen makes one. Every other way it
  // changes — a heading, a filter, the search box, the pager — asks again from
  // the store, because each of those is a different question for the server.
  useEffect(() => {
    void loadJobList();
  }, [loadJobList]);

  const order = st.jobSort === 'board' ? '' : SORTABLE[st.jobSort][st.jobDir];

  return (
    /* A frame, not a page that grows — the same mechanism the job sheet and the
       item editor use, and just as easy to undo by accident. `Shell`'s <main>
       is the only thing that scrolls vertically, so this section is `flex: 1`
       with a `minHeight: 0` floor, the card below fills what is left, and the
       rows scroll *inside* it. That is what keeps the headings and the pager on
       screen: a table that grew past the fold put both of them where nobody
       could reach them. Below the floor the page scrolls again, rather than
       squeezing the list into a slot. */
    <section
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        /* The page gutter and the foot. The band above takes its sides from
           this and adds only its own two vertical paddings — the gutter is the
           chrome's own, so the title lines up with the brand and the
           destinations over it, and the card under it. */
        padding: withGutter(gutter, 0, 20),
      }}
    >
      {/* The count used to sit under the title as the 1440px artboard has it,
          and it cost the table a row of jobs for a fact that reads just as well
          beside the word Jobs. `PageHead` is that row, shared with every other
          destination — the search and the filters are children of it rather
          than of a block of their own. */}
      <PageHead
        title="Jobs"
        caption={
          <>
            {/* The count is the server's, not the page's: "5 of 340" is the
                question the office is asking, and the 25 on screen are not. */}
            {st.listTotal} of {st.listVisible} jobs
            {st.q ? ` matching “${st.q}”` : ''}
            {order ? ` · ${order}` : ''}
          </>
        }
      >
        <input
          className="input"
          value={st.q}
          autoComplete={NO_AUTOFILL}
          onChange={(e) => st.setJobQuery(e.target.value)}
          placeholder="Job code, customer, colour, their ref"
          aria-label="Search jobs"
          /* 200 is where it stops being a search box and 320 what it is drawn
             at — and the floor has to be the *basis*, not a `minWidth` under a
             basis of 320. Flex breaks a line on the basis, so a basis of 320
             sends the filters to a second row rather than ever shrinking this;
             a basis of 200 puts the break where the box stops working and lets
             it grow back into whatever the row has left. */
          style={{
            marginLeft: 'auto',
            flex: '1 1 200px',
            maxWidth: 320,
            height: 44,
            fontSize: 16,
          }}
        />
        <Seg
          height={44}
          options={FILTERS.map((f) => ({
            label: f.label,
            on: st.jobFilter === f.key,
            pick: () => st.setJobFilter(f.key),
          }))}
        />
      </PageHead>

      <div
        className="blueprint"
        style={{
          background: 'var(--surface)',
          padding: 0,
          flex: '1 1 auto',
          minHeight: 260,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* The one thing that scrolls on this screen, both ways: `minHeight: 0`
            is what lets it shrink inside the card instead of growing past the
            bottom of it. The headings are sticky, so the column you sorted by
            is still named a hundred rows down. */}
        <div style={{ flex: '1 1 auto', minHeight: 0, overflow: 'auto' }}>
          <table className="table table-sticky" style={{ width: '100%', minWidth: 1080 }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 16, width: 46 }}>Colour</th>
                <JobSortHeader column="code" />
                <th>Customer</th>
                <th>Stage</th>
                <JobSortHeader column="promised" />
                <JobSortHeader column="deadline" />
                <th style={{ textAlign: 'right' }}>Items</th>
                <th style={{ textAlign: 'right' }}>m²</th>
                <th style={{ paddingRight: 16 }}>On it</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((j) => {
                const tone = toneOf(st, j.stage);
                return (
                  <tr key={j.code} style={{ cursor: 'pointer' }} onClick={() => st.openJob(j.code)}>
                    <td style={{ paddingLeft: 16 }}>
                      <Swatch code={j.colour} size={26} />
                    </td>
                    <td>
                      <div style={{ ...cell, fontWeight: 500 }}>{j.code}</div>
                      <div
                        style={{
                          fontSize: 13,
                          color: 'var(--ink-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {j.title}
                      </div>
                    </td>
                    <td style={{ fontSize: 13.5 }}>{j.customer}</td>
                    <td>
                      <span
                        style={{
                          fontFamily: 'var(--font-heading)',
                          fontSize: 9.5,
                          fontWeight: 600,
                          letterSpacing: '0.09em',
                          textTransform: 'uppercase',
                          padding: '3px 8px',
                          borderRadius: 999,
                          whiteSpace: 'nowrap',
                          background: tone
                            ? `color-mix(in srgb, ${tone} 18%, var(--ground))`
                            : 'var(--line-soft)',
                          color: 'var(--ink)',
                        }}
                      >
                        {stageName(st, j.stage)}
                      </span>
                    </td>
                    <td
                      style={{
                        ...cell,
                        color: isOverdue(j, st.stages) ? 'var(--urgent)' : 'var(--ink)',
                      }}
                    >
                      {formatDay(j.promised)}
                    </td>
                    <td
                      style={{
                        ...cell,
                        color: atRisk(j.plan) ? 'var(--urgent)' : 'var(--ink-muted)',
                      }}
                    >
                      {formatDay(j.plan.deadline)}
                    </td>
                    <td
                      style={{ textAlign: 'right', fontFamily: 'var(--font-data)', fontSize: 13 }}
                    >
                      {j.items}
                    </td>
                    <td
                      style={{
                        textAlign: 'right',
                        fontFamily: 'var(--font-data)',
                        fontSize: 13,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {areaFor(j).toFixed(1)}
                    </td>
                    <td
                      style={{
                        paddingRight: 16,
                        fontFamily: 'var(--font-data)',
                        fontSize: 11.5,
                        color: 'var(--ink-muted)',
                      }}
                    >
                      {j.who.length ? j.who.join(' ') : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {rows.length === 0 ? (
            <p
              style={{ margin: 0, padding: '20px 16px', fontSize: 13.5, color: 'var(--ink-muted)' }}
            >
              {status === 'loading'
                ? 'Reading the shop’s jobs…'
                : status === 'error'
                  ? st.listError
                  : st.q
                    ? `Nothing matches “${st.q}”. Try a job code, a customer, or a colour code like PN1G7.`
                    : st.jobFilter === 'all'
                      ? 'No jobs yet. Add the first one from the board.'
                      : `Nothing is ${FILTERS.find((f) => f.key === st.jobFilter)?.label.toLowerCase()} right now. Pick All to see every job.`}
            </p>
          ) : null}
        </div>

        <ListFoot />
      </div>
    </section>
  );
}
